/**
 * Device id — one stable UUID per browser, persisted to localStorage.
 *
 * Sent as the `X-Device-Id` header on every API request (see apiClient.js)
 * so the backend can gate staff sign-ins to admin-approved devices
 * (STAFF-DEVICE-LOCK). Fully resilient: private-mode / storage-blocked
 * browsers fall back to an in-memory id rather than breaking the app.
 *
 * NOTE: does NOT live in STORAGE_KEYS (src/constants/index.js) on purpose —
 * `clearAuthToken()` / cross-tab logout wipe every STORAGE_KEYS entry, but
 * the device id must survive sign-out and sign-in on the same browser.
 */

export const DEVICE_ID_STORAGE_KEY = 'ak_device_id';

// Memoized after the first read/mint so repeated calls (one per outgoing
// request) never touch localStorage more than once per page load.
let _cachedDeviceId = null;

function _generateUuidV4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback for environments without crypto.randomUUID: build a v4 UUID
  // from crypto.getRandomValues (widely supported, incl. older Safari).
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    // Last-resort fallback — Math.random is not cryptographically strong,
    // but a device id only needs to be unique, not unguessable.
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Get this browser's device id, minting and persisting one on first use.
 * Safe to call from anywhere (SSR, private mode, storage-blocked) — never
 * throws and always returns a usable id.
 * @returns {string} UUID v4
 */
export function getOrCreateDeviceId() {
  if (_cachedDeviceId) return _cachedDeviceId;

  if (typeof window === 'undefined') {
    _cachedDeviceId = _generateUuidV4();
    return _cachedDeviceId;
  }

  try {
    const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing) {
      _cachedDeviceId = existing;
      return _cachedDeviceId;
    }

    const minted = _generateUuidV4();
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, minted);
    _cachedDeviceId = minted;
    return _cachedDeviceId;
  } catch {
    // Storage unavailable/blocked (e.g. Safari private mode) — fall back to
    // an in-memory id for the rest of this page load. Never throw.
    _cachedDeviceId = _generateUuidV4();
    return _cachedDeviceId;
  }
}
